import { ButtonHTMLAttributes, FC, forwardRef, ReactElement } from "react";
import { cva, VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { I18nKeyType } from "@/i18n";
import { useTranslate } from "@/i18n/useTranslate.ts";
import { cn } from "@/utils/tailwind.ts";

const button = cva(
  "text-sm md:text-base font-inter min-w-40 rounded-sm inline-flex flex-row items-center justify-center gap-2 transition-colors cursor-pointer py-2 px-6 font-medium",
  {
    variants: {
      variant: {
        filled: "text-white",
        outline: "border bg-white",
      },
      color: {
        primary: "",
        secondary: "",
        neutral: "",
      },
      disabled: {
        false: null,
        true: "bg-ind-grey-50 border-0 text-ind-grey-400 cursor-not-allowed",
      },
    },
    compoundVariants: [
      {
        variant: "filled",
        color: "primary",
        disabled: false,
        className: "bg-primary-500 hover:bg-primary-600 active:bg-primary-900",
      },
      {
        variant: "filled",
        color: "secondary",
        disabled: false,
        className:
          "bg-secondary-600 hover:bg-secondary-700 active:bg-secondary-900",
      },
      {
        variant: "filled",
        color: "neutral",
        disabled: false,
        className:
          "bg-ind-grey-600 hover:bg-idn-grey-700 active:bg-ind-grey-900",
      },
      {
        variant: "outline",
        color: "primary",
        disabled: false,
        className:
          "border-primary-500 text-primary-500 hover:bg-primary-50 active:bg-primary-200",
      },
      {
        variant: "outline",
        color: "secondary",
        disabled: false,
        className:
          "border-secondary-500 text-secondary-500 hover:bg-secondary-50 active:bg-secondary-200",
      },
      {
        variant: "outline",
        color: "neutral",
        disabled: false,
        className:
          "border-ind-grey-500 text-ind-grey-500 hover:bg-ind-grey-50 active:bg-ind-grey-100",
      },
    ],
    defaultVariants: {
      variant: "filled",
      color: "primary",
      disabled: false,
    },
  },
);

export type SvButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "disabled"
> &
  VariantProps<typeof button> & {
    iconLeft?: ReactElement;
    iconRight?: ReactElement;
  } & (
    | {
        children: ReactElement | ReactElement[];
        i18nKey?: never;
        i18nOptions?: never;
      }
    | {
        children?: never;
        i18nKey: I18nKeyType;
        i18nOptions?: object;
      }
  );

export const SvButton: FC<SvButtonProps> = forwardRef<
  HTMLButtonElement,
  SvButtonProps
>(
  (
    {
      className,
      variant,
      color,
      disabled,
      children,
      i18nKey,
      i18nOptions,
      ...props
    },
    ref,
  ) => {
    const hasChildren = Boolean(children);

    const Comp = hasChildren ? Slot.Root : "button";
    const i18nText = useTranslate(i18nKey, i18nOptions);

    return (
      <Comp
        className={cn(button({ variant, color, disabled }), className)}
        ref={ref}
        {...props}>
        {props.iconLeft}
        <Slot.Slottable>{hasChildren ? children : i18nText}</Slot.Slottable>
        {props.iconRight}
      </Comp>
    );
  },
);
SvButton.displayName = "SvButton";
